import { useCallback } from 'react';
import { Box, Card, CardContent, Typography, FormControlLabel, Checkbox, Slider } from '@mui/material';
import { Tune as TuneIcon } from '@mui/icons-material';
import { ReportLayoutEditorProps, SECTION_OPTIONS } from './types';

export const ReportLayoutEditor = ({ settings, onChange, withheldSections = {} }: ReportLayoutEditorProps) => {
  const sec = settings.sections || {};
  const fm = settings.feature_matrix || {};
  const typo = settings.typography || {};
  const spacing = settings.spacing || {};

  const updateSection = useCallback(
    (key: string, checked: boolean) => {
      onChange({
        ...settings,
        sections: { ...sec, [key]: checked }
      });
    },
    [settings, sec, onChange]
  );

  const updateNested = useCallback(
    (group: string, key: string, value: number) => {
      onChange({
        ...settings,
        [group]: {
          ...(((settings as unknown as Record<string, unknown>)[group] as Record<string, unknown>) || {}),
          [key]: value
        }
      });
    },
    [settings, onChange]
  );

  return (
    <Box className="flex flex-col gap-4">
      {/* Section Visibility */}
      <Card variant="outlined">
        <CardContent>
          <Box className="flex items-center gap-2 mb-3">
            <TuneIcon fontSize="small" />
            <Typography variant="subtitle2">Section Visibility</Typography>
          </Box>
          <Box className="grid grid-cols-2 gap-1">
            {SECTION_OPTIONS.map((opt) => {
              const withheld = opt.key in withheldSections;
              return (
                <FormControlLabel
                  key={opt.key}
                  disabled={withheld}
                  control={
                    <Checkbox
                      size="small"
                      checked={!withheld && sec[opt.key] !== false}
                      onChange={(e) => updateSection(opt.key, e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">{withheld ? `${opt.label} (step turned off)` : opt.label}</Typography>
                  }
                />
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Feature Matrix Controls */}
      <Card variant="outlined">
        <CardContent>
          <Typography
            variant="subtitle2"
            gutterBottom>
            Feature Matrix
          </Typography>
          <Box className="flex flex-col gap-3">
            <Box>
              <Typography
                variant="caption"
                color="textSecondary">
                Feature Column Width: {fm.feature_column_pct ?? 34}%
              </Typography>
              <Slider
                size="small"
                value={fm.feature_column_pct ?? 34}
                onChange={(_, v) => updateNested('feature_matrix', 'feature_column_pct', v as number)}
                min={20}
                max={60}
                step={1}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="textSecondary">
                Supplier Column Min Width: {(fm.supplier_min_in ?? 1.1).toFixed(2)} in
              </Typography>
              <Slider
                size="small"
                value={fm.supplier_min_in ?? 1.1}
                onChange={(_, v) => updateNested('feature_matrix', 'supplier_min_in', v as number)}
                min={0.6}
                max={2.0}
                step={0.05}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="textSecondary">
                Cell Padding: {fm.cell_padding_px ?? 8}px
              </Typography>
              <Slider
                size="small"
                value={fm.cell_padding_px ?? 8}
                onChange={(_, v) => updateNested('feature_matrix', 'cell_padding_px', v as number)}
                min={4}
                max={20}
                step={1}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Typography & Spacing */}
      <Card variant="outlined">
        <CardContent>
          <Typography
            variant="subtitle2"
            gutterBottom>
            Typography & Spacing
          </Typography>
          <Box className="flex flex-col gap-3">
            <Box>
              <Typography
                variant="caption"
                color="textSecondary">
                Base Scale: {(typo.base_scale ?? 1.0).toFixed(2)}x
              </Typography>
              <Slider
                size="small"
                value={typo.base_scale ?? 1.0}
                onChange={(_, v) => updateNested('typography', 'base_scale', v as number)}
                min={0.8}
                max={1.5}
                step={0.05}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="textSecondary">
                Section Spacing: {spacing.section_spacing_px ?? 60}px
              </Typography>
              <Slider
                size="small"
                value={spacing.section_spacing_px ?? 60}
                onChange={(_, v) => updateNested('spacing', 'section_spacing_px', v as number)}
                min={20}
                max={120}
                step={2}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
